import React, { useState, useEffect, useCallback } from 'react';
import { userService } from '../../services/api';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { permissionService } from '../../services/permissionService';
import { roleService, Role } from '../../services/roleService';
import { User } from '../../types/auth';
import { DepartmentSummary } from '../../types/domain';
import DismissibleError from '../DismissibleError';
import { Edit3, LockOpen, Plus, Save, UserCheck, UserX, X } from 'lucide-react';

// TODO: Add user search by username and email
// TODO: Implement basic filtering by role and status
// TODO: Display user list with pagination
// TODO: Add bulk user operations (delete, activate)
// TODO: Add user detail view with activity

interface UserFormData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  password?: string;
  role: string;
  is_active: boolean;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [departmentSelections, setDepartmentSelections] = useState<Record<number, string>>({});
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    role: 'ACCOUNTABLE_PNP_OFFICER',
    is_active: true
  });
  
  const { error, setError, clearError, getErrorMessage } = useErrorHandler();
  const getFieldError = (field: keyof UserFormData) => (
    error?.validationErrors
      ?.filter((validationError) => validationError.field === field)
      .map((validationError) => validationError.message)
      .join(' ') || ''
  );
  const renderFieldError = (field: keyof UserFormData) => {
    const message = getFieldError(field);
    return message ? <span className="field-error" role="alert">{message}</span> : null;
  };
  const isLockedAccount = (user: User) => Boolean(user.is_locked || user.lockout_until);
  const formatLockoutUntil = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  // Load roles on component mount
  useEffect(() => {
    let mounted = true;
    roleService.loadRoles().then((loadedRoles) => {
      if (mounted) setRoles(loadedRoles);
    });
    userService.getDepartments().then((loadedDepartments) => {
      if (mounted) setDepartments(loadedDepartments);
    }).catch(() => undefined);
    return () => {
      mounted = false;
    };
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      clearError();
      const response = await userService.getUsers();
      setUsers(response.results || response);
    } catch (err) {
      setError(err as any);
    } finally {
      setLoading(false);
    }
  }, [clearError, setError]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      clearError();
      
      if (editingUser) {
        await userService.updateUser(editingUser.id, {
          username: formData.username,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role,
          is_active: formData.is_active,
        });
      } else {
        await userService.createUser({
          username: formData.username,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          password: formData.password,
          role: formData.role,
          is_active: formData.is_active,
        });
      }
      
      setShowCreateForm(false);
      setEditingUser(null);
      resetForm();
      await loadUsers();
    } catch (err) {
      setError(err as any);
    }
  };

  const handleEdit = (user: User) => {
    clearError();
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      password: '',
      role: user.role_code || 'ACCOUNTABLE_PNP_OFFICER',
      is_active: user.is_active
    });
    setShowCreateForm(true);
  };

  const toggleUserActive = async (user: User) => {
    try {
      clearError();
      if (user.is_active) {
        await userService.deactivateUser(user.id);
      } else {
        await userService.activateUser(user.id);
      }
      loadUsers();
    } catch (err: any) {
      setError(err);
    }
  };

  const unlockUser = async (user: User) => {
    const confirmed = window.confirm(`Unlock account ${user.username}?`);
    if (!confirmed) return;
    try {
      clearError();
      await userService.unlockUser(user.id);
      await loadUsers();
    } catch (err: any) {
      setError(err);
    }
  };

  const canAssignDepartment = (user: User) =>
    user.role_code === 'ACCOUNTABLE_PNP_OFFICER' || user.role_code === 'CENTRAL_LOGISTICS_OFFICER';

  const hasDepartmentSelection = (userId: number): boolean => (
    Object.prototype.hasOwnProperty.call(departmentSelections, userId)
  );

  const selectedDepartmentValue = (user: User): string => {
    if (hasDepartmentSelection(user.id)) return departmentSelections[user.id];
    return user.primary_department ? String(user.primary_department.id) : '';
  };

  type DepartmentAction = 'assign' | 'reassign' | 'unassign' | 'none';

  const getDepartmentAction = (user: User): DepartmentAction => {
    const selected = selectedDepartmentValue(user);
    const current = user.primary_department ? String(user.primary_department.id) : '';
    if (selected === current) return 'none';
    if (selected === '') return current ? 'unassign' : 'none';
    return current ? 'reassign' : 'assign';
  };

  const departmentActionLabel = (user: User): string => {
    switch (getDepartmentAction(user)) {
      case 'assign': return 'Assign';
      case 'reassign': return 'Reassign';
      case 'unassign': return 'Unassign';
      default: return 'No change';
    }
  };

  const applyDepartmentChange = async (user: User) => {
    const action = getDepartmentAction(user);
    const selected = selectedDepartmentValue(user);
    if (action === 'none') return;
    if (
      action === 'unassign'
      && !window.confirm(
        `Unassign ${user.username} from their department? The account will remain active but will lose department-scoped operational access.`
      )
    ) return;

    try {
      clearError();
      if (action === 'unassign') {
        await userService.unassignDepartment(user.id);
      } else {
        await userService.assignDepartment(user.id, Number(selected));
      }
      setDepartmentSelections((current) => {
        const next = { ...current };
        delete next[user.id];
        return next;
      });
      await loadUsers();
    } catch (err) {
      setError(err as any);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      role: 'ACCOUNTABLE_PNP_OFFICER',
      is_active: true
    });
  };

  const closeForm = () => {
    clearError();
    setShowCreateForm(false);
    setEditingUser(null);
    resetForm();
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="user-management">
      <div className="page-header">
        <h1>User Management</h1>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateForm(true)}
        >
          <Plus size={16} />Create User
        </button>
      </div>

      {error && <DismissibleError message={getErrorMessage()} onDismiss={clearError} />}

      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="user-management-modal-title">
            <div className="modal-header">
              <h2 id="user-management-modal-title">{editingUser ? 'Edit User' : 'Create New User'}</h2>
              <button className="close-btn" onClick={closeForm} aria-label="Close dialog"><X size={20} /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>Username *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    required
                  />
                  {renderFieldError('username')}
                </div>
                
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                  {renderFieldError('email')}
                </div>
                
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  />
                  {renderFieldError('first_name')}
                </div>
                
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  />
                  {renderFieldError('last_name')}
                </div>
                
                {!editingUser && (
                  <div className="form-group">
                    <label>Password *</label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required
                    />
                    {renderFieldError('password')}
                  </div>
                )}
                
                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                  >
                    {roles.map((role) => (
                      <option key={role.id} value={role.code}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                  {renderFieldError('role')}
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    />
                    Active
                  </label>
                  {renderFieldError('is_active')}
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? <Save size={16} /> : <Plus size={16} />}
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Department</th>
              <th>Status</th>
              <th>Staff</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.username}</td>
                <td>{user.email}</td>
                <td>{user.first_name} {user.last_name}</td>
                <td>{user.role_display}</td>
                <td>
                  {canAssignDepartment(user) ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <select
                        value={selectedDepartmentValue(user)}
                        onChange={(event) => setDepartmentSelections({
                          ...departmentSelections,
                          [user.id]: event.target.value,
                        })}
                        aria-label={`Department for ${user.username}`}
                      >
                        <option value="">Not assigned</option>
                        {departments.filter((department) => department.is_active !== false).map((department) => (
                          <option key={department.id} value={department.id}>{department.name}</option>
                        ))}
                      </select>
                      <button
                        className="btn btn-sm btn-secondary"
                        type="button"
                        disabled={getDepartmentAction(user) === 'none'}
                        onClick={() => applyDepartmentChange(user)}
                      >
                        {departmentActionLabel(user)}
                      </button>
                    </div>
                  ) : (
                    user.primary_department?.name || 'All Departments'
                  )}
                </td>
                <td>
                  <div className="status-stack">
                    <span className={`status ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {isLockedAccount(user) && <span className="status locked">Locked</span>}
                    {user.lockout_until && <small className="lockout-until">Until {formatLockoutUntil(user.lockout_until)}</small>}
                  </div>
                </td>
                <td>{user.is_staff ? 'Yes' : 'No'}</td>
                <td>
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleEdit(user)}
                  >
                    <Edit3 size={13} />Edit
                  </button>
                  {isLockedAccount(user) && (
                    <button
                      className="btn btn-sm btn-secondary"
                      type="button"
                      onClick={() => unlockUser(user)}
                    >
                      <LockOpen size={13} />Unlock
                    </button>
                  )}
                  {permissionService.hasPermission('admin.user.manage') && (
                    <button 
                      className={`btn btn-sm ${user.is_active ? 'btn-danger' : 'btn-secondary'}`}
                      onClick={() => toggleUserActive(user)}
                    >
                      {user.is_active ? <UserX size={13} /> : <UserCheck size={13} />}
                      {user.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagement;
